import React, { useEffect, useState, useMemo } from "react";
import AdminModify from "./AdminModify";
import axios from "axios";
import {
  FaSpinner, FaSearch, FaTimes, FaEdit, FaUser, FaCalendar,
  FaClock, FaDownload, FaFilter, FaExclamationTriangle, FaEye, FaFileAlt
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const AdminGetReporterRejectedAds = () => {
  const [ads, setAds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;
  const [showModifyModalAdId, setShowModifyModalAdId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRejectedAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/all/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const adsArray = Array.isArray(response.data) ? response.data : [];
        let filtered = [];
        adsArray.forEach((ad) => {
          if (Array.isArray(ad.acceptRejectReporterList)) {
            ad.acceptRejectReporterList.forEach((rep) => {
              if (rep.adProof === false && rep.accepted === false) {
                filtered.push({
                  adId: ad._id,
                  reporterId: rep.reporterId,
                  rejectNote: rep.rejectNote,
                  date: ad.createdAt,
                  time: ad.createdAt,
                  acceptRejectReporterListLength: ad.acceptRejectReporterList.length,
                  requiredReporter: ad.requiredReporter,
                });
              }
            });
          }
        });
        setAds(filtered);
        setCurrentPage(1);
      } catch (err) {
        setError("Failed to fetch rejected ads");
      } finally {
        setLoading(false);
      }
    };
    fetchRejectedAds();
  }, []);

  const handleModifyAd = (id) => {
    navigate("/admin/advertisement/modify", { state: { adId: id } });
  }

  const filteredAds = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return ads;
    return ads.filter((ad) => {
      return (
        ad.adId?.toLowerCase().includes(q) ||
        ad.reporterId?.toLowerCase().includes(q) ||
        ad.rejectNote?.toLowerCase().includes(q)
      );
    });
  }, [ads, searchText]);

  const { groupedCurrentRows, totalPages } = useMemo(() => {
    const totalPages = Math.ceil(filteredAds.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const currentRows = filteredAds.slice(startIndex, startIndex + rowsPerPage);

    const grouped = currentRows.reduce((acc, item) => {
      acc[item.adId] = acc[item.adId] || [];
      acc[item.adId].push(item);
      return acc;
    }, {});

    return {
      groupedCurrentRows: Object.entries(grouped),
      totalPages
    };
  }, [filteredAds, currentPage, rowsPerPage]);

  return (
    <>
      {/* Header Section */}
      <div className="bg-gradient-to-r from-red-600 via-rose-600 to-pink-700 rounded-xl md:rounded-2xl shadow-xl p-4 md:p-8 mb-6 md:mb-8 text-white relative overflow-hidden mx-2 md:mx-0 mt-[-10px] md:mt-0">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 md:gap-6">
            <div>
              <h1 className="text-xl md:text-3xl lg:text-4xl font-black mb-1 md:mb-2 flex items-center gap-2 md:gap-3 uppercase tracking-tight">
                <FaTimes className="text-red-200 w-5 h-5 md:w-8 md:h-8" />
                Rejected Ads
              </h1>
              <p className="text-red-100 text-[10px] md:text-sm font-medium opacity-80 uppercase tracking-widest">
                Reporter Audit Failures
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-2 md:gap-4">
                <div className="bg-white/20 backdrop-blur-md rounded-xl px-3 py-1.5 md:px-5 md:py-3 border border-white/10">
                  <span className="text-[8px] md:text-xs text-red-100 font-black uppercase tracking-widest block mb-0.5 md:mb-1">Total Rejected</span>
                  <div className="text-lg md:text-3xl font-black text-white leading-none">{filteredAds.length}</div>
                </div>
                <div className="bg-white/20 backdrop-blur-md rounded-xl px-3 py-1.5 md:px-5 md:py-3 border border-white/10 text-right">
                  <span className="text-[8px] md:text-xs text-red-100 font-black uppercase tracking-widest block mb-0.5 md:mb-1">Page Results</span>
                  <div className="text-lg md:text-3xl font-black text-white leading-none">
                    {Math.min(rowsPerPage, filteredAds.length)}
                  </div>
                </div>
              </div>
            </div>

            {/* Compact Navigation Buttons */}
            <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-3">
              {[
                { label: 'Pending', color: 'bg-yellow-500', icon: FaClock, path: '/admin/pendingposts' },
                { label: 'Approved', color: 'bg-emerald-500', icon: FaEdit, path: '/admin/approvedposts' },
                { label: 'Running', color: 'bg-blue-500', icon: FaEye, path: '/admin/runningposts' },
                { label: 'Completed', color: 'bg-purple-500', icon: FaEdit, path: '/admin/completedposts' }
              ].map((btn) => (
                <button
                  key={btn.label}
                  className={`${btn.color} hover:brightness-110 text-white px-3 py-2 md:px-5 md:py-3 rounded-xl font-black text-[9px] md:text-xs uppercase tracking-widest shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2`}
                  onClick={() => navigate(btn.path)}
                >
                  <btn.icon className="w-3 h-3 md:w-4 md:h-4" />
                  {btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modern Search Section */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl shadow-gray-200/40 border border-white p-3 md:p-6 mb-6 md:mb-8 mx-2 md:mx-0">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-red-500 text-gray-400">
              <FaSearch className="w-4 h-4" />
            </div>
            <input
              type="text"
              className="w-full pl-11 pr-4 py-3.5 md:py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-red-500/10 focus:border-red-500/50 focus:bg-white transition-all outline-none text-xs md:text-base font-bold shadow-inner"
              placeholder="Search by ID, Reporter, or Note..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-[54px] flex items-center gap-2 bg-gray-100/50 px-4 rounded-2xl border border-gray-200/50">
              <div className="text-center">
                <div className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Page</div>
                <div className="text-xs font-black text-gray-700">{currentPage} of {totalPages || 1}</div>
              </div>
              <div className="w-px h-6 bg-gray-200 mx-1"></div>
              <div className="text-center">
                <div className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Found</div>
                <div className="text-xs font-black text-gray-700">{filteredAds.length}</div>
              </div>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="h-[54px] px-6 bg-red-600 text-white rounded-2xl font-black uppercase tracking-[0.15em] text-xs hover:bg-black transition-all flex items-center gap-2 shadow-lg active:scale-95"
            >
              <FaDownload className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-2 md:mx-0 pb-20">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="relative w-12 h-12 md:w-16 md:h-16">
              <div className="absolute inset-0 rounded-full border-4 border-emerald-100 border-t-red-600 animate-spin"></div>
              <div className="absolute inset-2 rounded-full border-4 border-transparent border-t-rose-400 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-100 rounded-3xl p-6 md:p-12 text-center max-w-2xl mx-auto shadow-xl">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FaExclamationTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl md:text-2xl font-black text-gray-800 uppercase tracking-tight mb-2">Sync Error</h2>
            <p className="text-gray-500 font-medium mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="bg-red-600 text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-red-600/20 active:scale-95 transition-all">Try Again</button>
          </div>
        ) : filteredAds.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8 md:p-20 text-center">
            <div className="w-20 h-20 md:w-28 md:h-28 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
              <FaTimes className="w-10 h-10 md:w-14 md:h-14 text-gray-200" />
            </div>
            <h3 className="text-xl md:text-3xl font-black text-gray-800 mb-2 uppercase tracking-tight">
              Empty Queue
            </h3>
            <p className="text-gray-400 text-sm md:text-lg font-medium max-w-md mx-auto">
              {searchText ? "No matches found for your current filters." : "All reporter audit data is processed. No rejections found."}
            </p>
          </div>
        ) : (
          <div className="space-y-6 md:space-y-10">
            {groupedCurrentRows.map(([adId, adRows]) => {
              const firstRow = adRows[0];
              return (
                <div key={adId} className="bg-white rounded-[2rem] shadow-xl shadow-gray-200/30 border border-gray-100 overflow-hidden group">
                  {/* Ad Header Dashboard Style */}
                  <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-4 md:px-8 py-5 md:py-8 text-white">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 md:w-16 md:h-16 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-600/20 border border-white/10 group-hover:rotate-6 transition-transform">
                          <FaFileAlt className="w-5 h-5 md:w-8 md:h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-base md:text-2xl font-black uppercase tracking-tight flex items-center gap-2">
                            Ad: {adId}
                            <span className="bg-red-500/20 text-red-400 text-[8px] md:text-[10px] px-2 py-0.5 rounded-full font-black border border-red-500/30">REJECTED</span>
                          </h3>
                          <div className="flex items-center gap-3 mt-1 underline decoration-red-500/50 underline-offset-4 decoration-2">
                            <p className="text-[10px] md:text-sm text-gray-400 font-black uppercase tracking-widest">
                              Failure: {firstRow?.acceptRejectReporterListLength || 0} / {firstRow?.requiredReporter || 0} Reporters
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleModifyAd(adId)}
                        className="h-10 md:h-14 px-5 md:px-8 bg-white/10 hover:bg-white text-white hover:text-gray-900 rounded-2xl flex items-center justify-center gap-3 transition-all font-black text-[10px] md:text-xs uppercase tracking-[0.2em] border border-white/20 active:scale-95"
                      >
                        <FaEdit className="w-3 h-3 md:w-4 md:h-4" />
                        Modify Ad
                      </button>
                    </div>
                  </div>

                  {/* Responsive Details Content */}
                  <div className="p-0 md:p-2">
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="min-w-full border-separate border-spacing-y-2 px-6">
                        <thead>
                          <tr className="text-[#94a3b8] text-[10px] font-black uppercase tracking-[0.2em]">
                            <th className="px-6 py-4 text-left">Reporter</th>
                            <th className="px-6 py-4 text-left">Internal Logic Note</th>
                            <th className="px-6 py-4 text-left">Timeline</th>
                            <th className="px-6 py-4 text-center">Audit Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adRows.map((item, idx) => (
                            <tr key={idx} className="bg-[#fcfdfe] hover:bg-white transition-all duration-300 rounded-xl group/row border border-transparent hover:border-red-100">
                              <td className="px-6 py-4 first:rounded-l-2xl">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm border border-gray-100 group-hover/row:border-red-200 transition-colors">
                                    <FaUser className="text-red-600" />
                                  </div>
                                  <div className="text-sm font-black text-[#1e293b] uppercase tracking-tight">{item.reporterId || "N/A"}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="max-w-md">
                                  <p className="text-[11px] font-bold text-[#64748b] leading-relaxed italic line-clamp-2">
                                    "{item.rejectNote || "No specific reason provided by audit logic."}"
                                  </p>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="text-center">
                                    <div className="text-[10px] font-black text-gray-900">{item.date ? new Date(item.date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : "N/A"}</div>
                                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Date</div>
                                  </div>
                                  <div className="w-px h-6 bg-gray-100"></div>
                                  <div className="text-center">
                                    <div className="text-[10px] font-black text-gray-900">{item.date ? new Date(item.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true }) : "N/A"}</div>
                                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Time</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 last:rounded-r-2xl">
                                <div className="flex justify-center">
                                  <button className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                                    <FaEye size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card Layout - Compact Version */}
                    <div className="md:hidden space-y-3 p-4">
                      {adRows.map((item, idx) => (
                        <div key={idx} className="bg-gray-50/50 rounded-2xl border border-gray-100 p-4 relative overflow-hidden group/mcard">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center">
                                <FaUser className="text-red-600" />
                              </div>
                              <div>
                                <div className="text-xs font-black text-gray-800 uppercase tracking-tight">{item.reporterId || "N/A"}</div>
                                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                                  Audit Fail: {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white/80 rounded-xl border border-gray-100 p-3 mb-1">
                            <p className="text-[10px] font-bold text-gray-500 italic leading-relaxed">
                              "{item.rejectNote || "Audit failed without specific note."}"
                            </p>
                          </div>
                          <div className="flex justify-end gap-2 text-[9px] font-black text-gray-300 uppercase tracking-widest">
                            <span>Time: {item.date ? new Date(item.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : 'N/A'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Global Floating Desktop Pagination */}
        {!loading && totalPages > 1 && (
          <div className="fixed bottom-0 left-0 right-0 md:relative bg-white/80 backdrop-blur-lg border-t border-gray-100 p-4 md:p-8 flex justify-center z-40">
            <nav className="inline-flex items-center gap-1 md:gap-2 bg-gray-100/50 p-1.5 rounded-2xl border border-gray-200 shadow-sm">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-md disabled:opacity-30 transition-all active:scale-90"
              >
                <FaClock className="rotate-90 text-[10px] md:text-sm" />
              </button>
              <div className="flex items-center gap-1 px-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 md:w-12 md:h-12 rounded-xl text-xs md:text-sm font-black transition-all ${currentPage === pageNum
                        ? 'bg-red-600 text-white shadow-lg shadow-red-500/20 rotate-3'
                        : 'text-gray-600 hover:bg-white'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && <span className="text-gray-400 px-2">...</span>}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-md disabled:opacity-30 transition-all active:scale-90"
              >
                <FaClock className="-rotate-90 text-[10px] md:text-sm" />
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Modal for AdminModify - Keep for legacy route sync if needed */}
      {showModifyModalAdId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-fadeIn">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden relative border border-gray-100 flex flex-col">
            <div className="bg-gray-900 p-6 flex justify-between items-center text-white">
              <h2 className="text-xl font-black uppercase tracking-tight">Campaign Modification</h2>
              <button
                className="text-white/60 hover:text-white text-3xl font-light w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-all"
                onClick={() => setShowModifyModalAdId(null)}
              >
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 md:p-8">
              <AdminModify adId={showModifyModalAdId} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminGetReporterRejectedAds;
